import React from "react";
import ImageCard from "./ImageCard";

const cardStyle = {
  position: "relative",
  background: "rgba(255,255,255,0.72)",
  border: "1px solid #e4e8ef",
  borderRadius: "12px",
  boxShadow: "0 8px 22px rgba(25, 35, 52, 0.08)",
  overflow: "hidden",
};

const cardBackdropStyle = (imageUrl) => ({
  position: "absolute",
  inset: "-18px",
  backgroundImage: imageUrl
    ? `linear-gradient(90deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.18) 18%, rgba(255,255,255,0.42) 44%, rgba(255,255,255,0.78) 72%, rgba(255,255,255,0.96) 100%), url(${imageUrl})`
    : "linear-gradient(90deg, rgba(255,255,255,0.98) 0%, rgba(248,250,253,1) 100%)",
  backgroundPosition: "left center",
  backgroundSize: "cover",
  backgroundRepeat: "no-repeat",
  filter: imageUrl ? "blur(22px) saturate(1.12) brightness(1.03)" : "none",
  transform: imageUrl ? "scale(1.12)" : "none",
  opacity: imageUrl ? 0.42 : 1,
  pointerEvents: "none",
});

const headerStyle = {
  position: "relative",
  zIndex: 1,
  display: "flex",
  alignItems: "center",
  gap: "16px",
  padding: "14px 16px",
  cursor: "pointer",
  background: "linear-gradient(135deg, rgba(255,255,255,0.50) 0%, rgba(247,249,252,0.80) 45%, rgba(255,255,255,0.90) 100%)",
  backdropFilter: "blur(6px)",
};

const posterFrontStyle = {
  position: "absolute",
  left: 0,
  top: 0,
  zIndex: 2,
};

const infoStyle = {
  flex: "1 1 auto",
  minWidth: 0,
  position: "relative",
  zIndex: 1,
};

const titleRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  flexWrap: "wrap",
};

const titleStyle = {
  fontSize: "24px",
  lineHeight: 1.05,
  fontWeight: 700,
  color: "#21252b",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
};

const assetStyle = {
  fontSize: "12px",
  fontWeight: 700,
  color: "#67707b",
  background: "#f3f5f8",
  border: "1px solid #e4e8ef",
  borderRadius: "999px",
  padding: "4px 10px",
  whiteSpace: "nowrap",
};

const badgeWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "8px",
  marginTop: "8px",
};

const badgeStyle = {
  fontSize: "11px",
  color: "#4e5968",
  background: "#eef2f7",
  border: "1px solid #e1e7ef",
  borderRadius: "999px",
  padding: "4px 10px",
  maxWidth: "100%",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const summaryStyle = {
  marginTop: "8px",
  fontSize: "13px",
  color: "#5f6875",
  lineHeight: 1.45,
  maxWidth: "100%",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const actionsStyle = {
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  paddingTop: "2px",
  position: "relative",
  zIndex: 1,
};

const actionButtonStyle = {
  width: "32px",
  height: "32px",
  borderRadius: "8px",
  border: "1px solid #d9dee7",
  background: "#fff",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#222",
  cursor: "pointer",
};

const bodyStyle = (expanded) => ({
  position: "relative",
  zIndex: 1,
  maxHeight: expanded ? "1200px" : "0px",
  opacity: expanded ? 1 : 0,
  overflow: "hidden",
  transition: "max-height 240ms ease, opacity 180ms ease, padding 240ms ease",
  borderTop: expanded ? "1px solid #e8edf4" : "1px solid transparent",
  padding: expanded ? "14px 16px 16px" : "0 16px",
  background: "linear-gradient(180deg, rgba(251,252,254,0.72) 0%, rgba(251,252,254,0.92) 100%)",
  backdropFilter: "blur(4px)",
});

const factStyle = {
  minWidth: 0,
  minHeight: "66px",
  padding: "10px 12px",
  borderRadius: "10px",
  background: "#f8f9fb",
  border: "1px solid #eceff4",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const factLabelStyle = {
  fontSize: "10px",
  fontWeight: 700,
  letterSpacing: "0.6px",
  textTransform: "uppercase",
  color: "#8a8f98",
  marginBottom: "4px",
};

const factValueStyle = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#2f3338",
  whiteSpace: "normal",
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  lineHeight: 1.25,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: "12px",
  alignItems: "stretch",
};

const sectionTitleStyle = {
  fontSize: "10px",
  fontWeight: 800,
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  color: "#7b8391",
  marginBottom: "12px",
};

const MediaHeroCard = ({
  imageUrl,
  title,
  assetId,
  badges = [],
  summary = "",
  facts = [],
  expanded = false,
  onToggleExpanded,
  actions = [],
  imageWidth = "68px",
  imageHeight = "92px",
}) => {
  const normalizedBadges = (badges || []).filter(Boolean);
  const normalizedFacts = (facts || []).filter((fact) => fact && (fact.label || fact.value));

  return (
    <div style={cardStyle}>
      <div style={cardBackdropStyle(imageUrl)} />
      <div
        style={headerStyle}
        onClick={() => {
          onToggleExpanded?.();
        }}
      >
        <div style={{
          flex: "0 0 auto",
          width: imageWidth,
          height: imageHeight,
          position: "relative",
          borderRadius: "12px",
          overflow: "hidden",
        }}>
          <ImageCard
            style={{
              ...posterFrontStyle,
              width: imageWidth,
              height: imageHeight,
              borderRadius: "12px",
              boxShadow: "0 8px 18px rgba(0,0,0,0.18)",
              border: "1px solid rgba(255,255,255,0.65)",
            }}
            width={imageWidth}
            height={imageHeight}
            img={imageUrl}
            alt={title ?? "Media"}
          />
        </div>

        <div style={infoStyle}>
          <div style={titleRowStyle}>
            <div style={titleStyle}>{title || "-"}</div>
            {assetId !== undefined && assetId !== null && `${assetId}` !== "" && (
              <span style={assetStyle}>#{assetId}</span>
            )}
          </div>

          {normalizedBadges.length > 0 && (
            <div style={badgeWrapStyle}>
              {normalizedBadges.map((badge) => (
                <span key={badge} style={badgeStyle}>
                  {badge}
                </span>
              ))}
            </div>
          )}

          {summary && <div style={summaryStyle}>{summary}</div>}
        </div>

        <div style={actionsStyle}>
          {actions.map((action, index) => (
            <button
              key={`${action.title || "action"}-${index}`}
              type="button"
              title={action.title}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.(e);
              }}
              style={actionButtonStyle}
            >
              <i className={action.icon} />
            </button>
          ))}

          {typeof onToggleExpanded === "function" && (
            <button
              type="button"
              title={expanded ? "Collapse" : "Expand"}
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpanded();
              }}
              style={actionButtonStyle}
            >
              <i className={`fa-solid fa-angle-${expanded ? "up" : "down"}`} />
            </button>
          )}
        </div>
      </div>

      <div style={bodyStyle(expanded)}>
        <div style={sectionTitleStyle}>More Details</div>
        <div style={gridStyle}>
          {normalizedFacts.map((fact, index) => (
            <div key={`${fact.label || "fact"}-${index}`} style={factStyle}>
              <div style={factLabelStyle}>{fact.label}</div>
              <div style={factValueStyle}>{fact.value || "-"}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MediaHeroCard);
